import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Printer, CheckCircle, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DeleteAllButton } from './DeleteAllButton';
import { formatCATDate } from '../../utils/timezone';
import { API, CLAIMS_PER_PAGE_DEFAULT, CLAIMS_PER_PAGE_FILTERED, toggleSelectItem, toggleSelectAll, errDetail } from './adminShared';

export default function ClaimsTab({ hospitals, members, authCfg }) {
  const [claims, setClaims] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [hospitalFilter, setHospitalFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('PENDING');

  const [claimDialog, setClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState({ claim_id: '', patient_serial_number: '', claim_items: [] });
  const [claimHospitalName, setClaimHospitalName] = useState('');
  const [claimHospitalPricelist, setClaimHospitalPricelist] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState([]);

  const perPage = hospitalFilter && hospitalFilter !== 'all' ? CLAIMS_PER_PAGE_FILTERED : CLAIMS_PER_PAGE_DEFAULT;

  const load = useCallback(async (p = 1, s = '', hf = 'all') => {
    try {
      const limit = hf && hf !== 'all' ? CLAIMS_PER_PAGE_FILTERED : CLAIMS_PER_PAGE_DEFAULT;
      const params = { limit, skip: (p - 1) * limit };
      if (hf && hf !== 'all') params.hospital = hf;
      if (s) params.search = s;
      const response = await axios.get(`${API}/admin/claims/all`, { ...authCfg, params });
      const data = response.data;
      const claimsArray = Array.isArray(data) ? data : (data.claims || []);
      setClaims(claimsArray);
      const totalCount = data.total || claimsArray.length;
      setTotal(totalCount);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (error) {
      setClaims([]);
      setTotal(0);
      setTotalPages(1);
    }
  }, [authCfg]);

  useEffect(() => { load(page, search, hospitalFilter); }, [page, search, hospitalFilter, load]);

  const refresh = () => load(page, search, hospitalFilter);

  const openEditDialog = async (claim) => {
    try {
      const response = await axios.get(`${API}/claims/${claim.claim_id}`, authCfg);
      const hospitalName = response.data.header.hospital_name || claim.hospital_name;
      setClaimHospitalName(hospitalName);
      try {
        const pricelistResponse = await axios.get(`${API}/admin/pricelists/all`, {
          ...authCfg,
          params: { hospital_filter: hospitalName, limit: 10000 },
        });
        const pricelistData = pricelistResponse.data.data || pricelistResponse.data.pricelists || pricelistResponse.data || [];
        setClaimHospitalPricelist(Array.isArray(pricelistData) ? pricelistData : []);
      } catch {
        setClaimHospitalPricelist([]);
      }
      setItemSearchQuery('');
      setItemSearchResults([]);
      setClaimForm({
        claim_id: claim.claim_id,
        patient_serial_number: response.data.header.patient_serial_number,
        status: response.data.header.status || 'PENDING',
        claim_items: response.data.details.map((item) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          cost: item.item_cost,
          quantity: item.quantity || 1,
          uom: item.uom || '',
        })),
      });
      setClaimDialog(true);
    } catch (error) {
      toast.error('Failed to load claim details');
    }
  };

  const handleItemSearch = (query) => {
    setItemSearchQuery(query);
    if (!query || query.length < 2) {
      setItemSearchResults([]);
      return;
    }
    const searchLower = query.toLowerCase();
    const results = claimHospitalPricelist.filter((item) =>
      (item.item_id && item.item_id.toLowerCase().includes(searchLower)) ||
      (item.item_name && item.item_name.toLowerCase().includes(searchLower))
    ).slice(0, 20);
    setItemSearchResults(results);
  };

  const handleAddItem = (item) => {
    const existingIndex = claimForm.claim_items.findIndex((ci) => ci.item_id === item.item_id);
    if (existingIndex >= 0) {
      const updatedItems = [...claimForm.claim_items];
      updatedItems[existingIndex].quantity = (updatedItems[existingIndex].quantity || 1) + 1;
      setClaimForm({ ...claimForm, claim_items: updatedItems });
      toast.success(`Increased quantity for ${item.item_name}`);
    } else {
      setClaimForm({
        ...claimForm,
        claim_items: [...claimForm.claim_items, { item_id: item.item_id, item_name: item.item_name, cost: item.cost || 0, quantity: 1, uom: item.uom || '' }],
      });
    }
    setItemSearchQuery('');
    setItemSearchResults([]);
  };

  const claimTotal = claimForm.claim_items.reduce((sum, item) => sum + ((item.cost || 0) * (item.quantity || 1)), 0);

  const handleClaimUpdate = async (e) => {
    e.preventDefault();
    if (claimForm.claim_items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.put(`${API}/admin/claims/${claimForm.claim_id}`, {
        patient_serial_number: claimForm.patient_serial_number,
        status: claimForm.status,
        claim_items: claimForm.claim_items.map((item) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_cost: item.cost,
          quantity: item.quantity || 1,
          uom: item.uom || '',
        })),
      }, authCfg);
      toast.success(response.data.message || 'Claim updated successfully');
      setClaimDialog(false);
      refresh();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to update claim'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (claimId) => {
    setConfirm({
      title: `Delete claim "${claimId}"`,
      description: 'This will refund the amount if the claim was pending.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/claims/${claimId}`, authCfg);
          toast.success('Claim deleted successfully');
          refresh();
        } catch (error) {
          toast.error('Failed to delete claim');
        }
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      title: `Delete ${selected.length} selected claim(s)`,
      description: 'This action cannot be undone. Pending claims will be refunded.',
      confirmLabel: `Delete ${selected.length}`,
      destructive: true,
      onConfirm: async () => {
        setLoading(true);
        let ok = 0, fail = 0;
        for (const id of selected) {
          try {
            await axios.delete(`${API}/admin/claims/${id}`, authCfg);
            ok++;
          } catch {
            fail++;
          }
        }
        toast.success(`Deleted ${ok} claim(s)${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelected([]);
        refresh();
        setLoading(false);
      },
    });
  };

  const handleBulkStatusChange = async () => {
    if (selected.length === 0 || !bulkStatus) {
      toast.error('No claims selected or status not chosen');
      return;
    }
    setLoading(true);
    let ok = 0, fail = 0;
    for (const claimId of selected) {
      try {
        await axios.put(`${API}/admin/claims/${claimId}/status`, { status: bulkStatus }, authCfg);
        ok++;
      } catch {
        fail++;
      }
    }
    toast.success(`Updated ${ok} claim(s)${fail > 0 ? `, ${fail} failed` : ''}`);
    setSelected([]);
    setBulkStatusDialog(false);
    refresh();
    setLoading(false);
  };

  return (
    <>
      <Card>
        <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <CardTitle>All Claims (View & Delete)</CardTitle>
            <div className="flex gap-2">
              <DeleteAllButton
                label="Delete All Claims"
                title="Delete ALL claims"
                description={'This permanently removes EVERY claim (headers and line items) from ALL hospitals.\nFamily balances and hospital deposits are NOT adjusted.\nThis cannot be undone.'}
                url={`${API}/admin/claims/all`}
                onDone={() => { setPage(1); load(1); }}
                testId="delete-all-claims"
              />
              {selected.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setBulkStatusDialog(true)}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Change Status ({selected.length})
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selected.length}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input placeholder="Search by exact Claim ID (CLAIM-XXXXXXXX) or Patient ID (SEC-XXXX-XX)..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10" data-testid="claim-search-input" />
            </div>
            <div className="w-full md:w-64">
              <Select value={hospitalFilter} onValueChange={(value) => { setHospitalFilter(value); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Filter by Hospital" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hospitals</SelectItem>
                  {hospitals.map((h) => (
                    <SelectItem key={h.hospital_name} value={h.hospital_name}>{h.hospital_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mb-2">
            {search && <p className="text-sm text-gray-500">Found {total} claim(s)</p>}
            {!search && total > 0 && (
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * perPage) + 1} - {Math.min(page * perPage, total)} of {total} claims
                {hospitalFilter !== 'all' && ` from ${hospitalFilter} (100 per page)`}
              </p>
            )}
          </div>
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-center p-3 text-sm font-semibold w-12">
                  <Checkbox
                    checked={selected.length === claims.length && claims.length > 0}
                    onCheckedChange={() => toggleSelectAll(claims, selected, setSelected, 'claim_id')}
                  />
                </th>
                <th className="text-left p-3 text-sm font-semibold">Claim ID</th>
                <th className="text-left p-3 text-sm font-semibold">Hospital</th>
                <th className="text-left p-3 text-sm font-semibold">Patient</th>
                <th className="text-left p-3 text-sm font-semibold">Date</th>
                <th className="text-right p-3 text-sm font-semibold">Amount</th>
                <th className="text-center p-3 text-sm font-semibold">Status</th>
                <th className="text-center p-3 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.claim_id} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-center">
                    <Checkbox
                      checked={selected.includes(claim.claim_id)}
                      onCheckedChange={() => toggleSelectItem(claim.claim_id, selected, setSelected)}
                    />
                  </td>
                  <td className="p-3 text-sm font-medium">{claim.claim_id}</td>
                  <td className="p-3 text-sm">{claim.hospital_name}</td>
                  <td className="p-3 text-sm">{claim.patient_name}</td>
                  <td className="p-3 text-sm">{formatCATDate(claim.timestamp)}</td>
                  <td className="p-3 text-sm text-right font-medium">$ {claim.total_claim_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      claim.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800'
                        : claim.status === 'PAID' ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {claim.status}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(claim)} className="text-green-600 hover:bg-green-50" title="Edit Claim">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/print/${claim.claim_id}`, '_blank')} className="text-blue-600 hover:bg-blue-50" title="Print Claim">
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(claim.claim_id)} className="text-red-600 hover:bg-red-50" title="Delete Claim">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </CardContent>
      </Card>

      <Dialog open={bulkStatusDialog} onOpenChange={setBulkStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Status for {selected.length} Claim(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={bulkStatus || 'PENDING'} onValueChange={setBulkStatus}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="VOIDED">Voided</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleBulkStatusChange} disabled={loading || !bulkStatus} className="w-full">
              {loading ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={claimDialog} onOpenChange={setClaimDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Claim - {claimForm.claim_id}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClaimUpdate} className="space-y-6">
            <div>
              <Label>Patient Serial Number</Label>
              <Select value={claimForm.patient_serial_number} onValueChange={(value) => setClaimForm({ ...claimForm, patient_serial_number: value })}>
                <SelectTrigger><SelectValue placeholder="Select Patient" /></SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.serial_number} value={member.serial_number}>
                      {member.serial_number} - {member.first_name} {member.last_name} (Family: {member.family_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Claim Status</Label>
              <Select value={claimForm.status || 'PENDING'} onValueChange={(value) => setClaimForm({ ...claimForm, status: value })}>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-yellow-400 rounded-full"></span>PENDING</span>
                  </SelectItem>
                  <SelectItem value="PAID">
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-green-500 rounded-full"></span>PAID</span>
                  </SelectItem>
                  <SelectItem value="VOIDED">
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-red-500 rounded-full"></span>VOIDED</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-600 mt-1">Changing status will automatically adjust family and hospital balances</p>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Claim Items</h3>
                <span className="text-sm text-gray-500">Hospital: {claimHospitalName} ({claimHospitalPricelist.length} items available)</span>
              </div>

              <div className="relative">
                <Label>Search Price List Items</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input placeholder={`Search items from ${claimHospitalName} price list...`} value={itemSearchQuery} onChange={(e) => handleItemSearch(e.target.value)} className="pl-10" data-testid="pricelist-search-input" />
                </div>
                {itemSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {itemSearchResults.map((item) => (
                      <div key={item.item_id} className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 flex justify-between items-center" onClick={() => handleAddItem(item)}>
                        <div>
                          <span className="font-medium text-sm">{item.item_id}</span>
                          <span className="text-gray-600 text-sm ml-2">{item.item_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 font-bold">${(item.cost || 0).toFixed(2)}</span>
                          <Plus className="w-4 h-4 text-blue-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {itemSearchQuery.length >= 2 && itemSearchResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-gray-500 text-center">
                    No items found matching "{itemSearchQuery}"
                  </div>
                )}
              </div>

              {claimForm.claim_items.length > 0 && (
                <table className="w-full border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2 text-sm">Item</th>
                      <th className="text-right p-2 text-sm">Unit Cost</th>
                      <th className="text-center p-2 text-sm w-24">Qty</th>
                      <th className="text-right p-2 text-sm">Total</th>
                      <th className="text-center p-2 text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimForm.claim_items.map((item, index) => (
                      <tr key={`${item.item_id || item.item_name}-${index}`} className="border-b">
                        <td className="p-2 text-sm">{item.item_name || 'Unknown Item'}</td>
                        <td className="p-2 text-sm text-right">${(item.cost || 0).toFixed(2)}</td>
                        <td className="p-2 text-sm text-center">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity || 1}
                            onChange={(e) => {
                              const newQuantity = parseInt(e.target.value) || 1;
                              const updatedItems = [...claimForm.claim_items];
                              updatedItems[index] = { ...updatedItems[index], quantity: newQuantity };
                              setClaimForm({ ...claimForm, claim_items: updatedItems });
                            }}
                            className="w-20 text-center"
                            data-testid={`claim-item-qty-${index}`}
                          />
                        </td>
                        <td className="p-2 text-sm text-right font-bold">${((item.cost || 0) * (item.quantity || 1)).toFixed(2)}</td>
                        <td className="p-2 text-center">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setClaimForm({ ...claimForm, claim_items: claimForm.claim_items.filter((_, i) => i !== index) })} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-blue-50">
                    <tr>
                      <td colSpan="3" className="p-2 text-sm font-bold">Total:</td>
                      <td className="p-2 text-sm text-right font-bold text-blue-600">${claimTotal.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setClaimDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Claim'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
